// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const InlineFrame = require('client-v2/common/components/inline-frame')

const YOUTUBE_DOMAIN_REGEXP = /\b(youtube|youtu\.be)\b/

// BEM Block entity definition
const bem = BemEntity('EmbeddedPlayer')

/**
 * @param {Object} props
 * @param {boolean} [props.isDisclosed]
 * @param {string} [props.className]
 * @param {string} props.src
 * @param {function} [props.onDisclose]
 *
 * If `isDisclosed`/`onDisclose` props are omitted,
 * the component will manage disclosure internally.
 */
function EmbeddedPlayer(props) {
  const [ isDisclosed, setDisclosed ] = useDisclosure(props)
  // Memoize event handler callback
  const onDisclose = React.useCallback(
    event => {
      event.stopPropagation()
      setDisclosed()
    },
    [ setDisclosed ]
  )
  if (!isDisclosed) {
    return (
      tag('div', {
        className: classNames(
          props.className,
          bem.block({
            curtain: true,
            youtube: YOUTUBE_DOMAIN_REGEXP.test(props.src)
          })
        ),
        onClick: onDisclose
      })
    )
  }
  return (
    tag(InlineFrame, {
      className: classNames(
        props.className,
        bem.block()
      ),
      src: props.src
    })
  )
}

EmbeddedPlayer.propTypes = {
  // Flags
  isDisclosed: PropTypes.bool,
  // Values
  className: PropTypes.string,
  src: PropTypes.string.isRequired,
  // Callbacks
  onDisclose: PropTypes.func
}

module.exports = EmbeddedPlayer

// Internal React hook

/**
 * @param {Object} props
 * @param {boolean} [props.isDisclosed]
 * @param {function} [props.onDisclose]
 */
function useDisclosure(props) {
  // Optionally manage disclosure with Component state
  const hasDisclosureProps = (
    props.isDisclosed === true ||
    typeof props.isDisclosed === 'boolean' &&
    typeof props.onDisclose === 'function'
  )
  const [ isDisclosedState, setDisclosedState ] = React.useState(false)
  const isDisclosed = hasDisclosureProps ? props.isDisclosed : isDisclosedState
  const setDisclosed = React.useCallback(
    () => hasDisclosureProps ? props.onDisclose() : setDisclosedState(true),
    [ hasDisclosureProps, props.onDisclose, setDisclosedState ]
  )
  return [ isDisclosed, setDisclosed ]
}
